import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, ShoppingBag } from "lucide-react";
import { subscriptionsService } from "../../lib/api/subscriptions";
import { useAuthStore } from "../../stores/authStore";

/**
 * Full-page lock screen shown to Shopify-billed accounts that hit the
 * suspended state (post-60-day read-only). Replaces the entire authenticated
 * UI until billing is restored.
 *
 * Rendered as a wrapper inside ProtectedRoute (above BillingBanner), so it
 * pre-empts any other rendering when active — EXCEPT the /plan route, which is
 * let through so a suspended account can recover via website (Stripe) billing.
 * A suspended account is "open": its Shopify sub has lapsed, so under the
 * one-active-subscription model it may start a new subscription on the website
 * OR reinstall on Shopify.
 */
export function SuspendedInterstitial({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation("common");
    const { isAuthenticated } = useAuthStore();
    const location = useLocation();
    const navigate = useNavigate();

    const { data } = useQuery({
        // Shares the cache entry with BillingBanner, PlanPage and UpgradeModal.
        queryKey: ["subscriptions", "billing-status"],
        queryFn: subscriptionsService.getBillingStatus,
        enabled: isAuthenticated,
        staleTime: 30_000,
    });

    const status = data?.data;
    const isSuspended =
        status?.billingSource === "shopify" && status?.billingStatus === "suspended";

    if (!isSuspended) return <>{children}</>;
    // Let suspended accounts reach the plans page to recover via the website.
    if (location.pathname === "/plan") return <>{children}</>;

    const appInstalled = !!status.appInstalled;

    const handleShopifyRestore = () => {
        // Use the configured install link (same as the banner) — after an
        // uninstall the app is gone from /admin/apps, so deep-linking there is a
        // dead end; the merchant needs the install/OAuth entry point.
        const config = (window as unknown as { __DASHBAL_CONFIG__?: { shopifyAppLink?: string } }).__DASHBAL_CONFIG__;
        const installUrl = config?.shopifyAppLink || "https://apps.shopify.com/dashbal";
        window.open(installUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="fixed inset-0 z-50 bg-background-page flex items-center justify-center p-4">
            <div className="card-elevated max-w-md w-full p-8 text-center space-y-5">
                <div className="mx-auto w-14 h-14 rounded-full bg-error-500/10 flex items-center justify-center">
                    <Lock className="w-7 h-7 text-error-500" />
                </div>
                <h2 className="text-h2 font-display font-semibold text-text-primary">
                    {t("billing.suspended.title", "Account suspended")}
                </h2>
                <p className="text-small text-neutral-700 dark:text-neutral-300">
                    {t(
                        "billing.suspended.body",
                        "Your account is suspended because billing wasn't restored in time. Restore billing on Shopify or start a new subscription on the website to reactivate.",
                    )}
                </p>
                <div className="flex flex-col gap-2 pt-2">
                    {!appInstalled && (
                        <button
                            type="button"
                            onClick={handleShopifyRestore}
                            className="btn-primary px-6 py-2.5 inline-flex items-center justify-center gap-2"
                        >
                            <ShoppingBag className="w-4 h-4" />
                            {t("billing.banner.reinstallShopify", "Reinstall on Shopify")}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate("/plan")}
                        className={`${appInstalled ? "btn-primary" : "btn-secondary"} px-6 py-2.5 inline-flex items-center justify-center gap-2`}
                    >
                        {t("billing.banner.subscribeWebsite", "Subscribe on the website")}
                    </button>
                    <p className="text-tiny text-neutral-500 dark:text-neutral-400 pt-2">
                        {t("billing.suspended.recoverHint", "Restore billing on Shopify, or start a new subscription on the website.")}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default SuspendedInterstitial;
